import {
  DATA_INTELLIGENCE_DOMAIN_ID,
  useDomainFromUrl,
} from '@/app/_common/domain/use-domain-from-url'
import { CraftAgentIcon } from '@/components/icons/craft-agent-icon'
import { Button, type ButtonProps } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { TextareaAutoSize } from '@/components/ui/textarea'
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button'
import { cn } from '@/lib/utils'
import { useTranslate } from '@tolgee/react'
import type { FormApi } from 'final-form'
import createDecorator from 'final-form-focus'
import { ArrowRightIcon } from 'lucide-react'
import React from 'react'
import { Field, Form } from 'react-final-form'
import type { TextareaAutosizeProps } from 'react-textarea-autosize'
import { useLocalStorage } from 'react-use'
import { AgentAnimation } from './agent-loader'
import { CancelTurnAnimation } from './cancel-turn-loader'
import { PromptFormFileUpload } from './prompt-form-file-upload'

type PromptFormValues = { prompt: string; files: FileList | null }
export type PromptFormApi = FormApi<PromptFormValues, Partial<PromptFormValues>>

export type CraftButtonState = 'loading' | 'enabled' | 'disabled' | null
type PromptFormProps = Readonly<{
  onSubmit: (values: PromptFormValues, form?: PromptFormApi) => void
  initialPrompt?: string
  disabled?: boolean
  disableSubmit?: boolean
  loading?: boolean
  renderFooter?: (form: PromptFormApi) => React.ReactNode
  clearOnSubmit?: boolean
  onCancelTurn?: (activeTurnId: string) => void
  fieldId: string
  buttonProps?: ButtonProps
  onCraftAgentClick?: () => void // Add this new prop
  craftButtonState?: CraftButtonState
  activeTurnId?: string | null
}>

const PromptForm = React.forwardRef<HTMLTextAreaElement, PromptFormProps>(
  (
    {
      initialPrompt,
      onSubmit,
      disabled,
      disableSubmit,
      loading,
      renderFooter,
      clearOnSubmit,
      onCancelTurn,
      fieldId,
      buttonProps: { className: buttonClassName, ...buttonProps } = {},
      onCraftAgentClick, // Add this
      craftButtonState = null,
      activeTurnId,
    },
    ref,
  ) => {
    const { t } = useTranslate()
    const { domainId } = useDomainFromUrl()
    const { deleteStoredValue } = usePromptLocalStorage(fieldId)
    const isSubmittable = !disabled && !disableSubmit && !loading
    const handleKeyDown = React.useCallback(
      (
        e: React.KeyboardEvent<HTMLTextAreaElement>,
        form: PromptFormApi,
        promptValue: string,
        valid: boolean,
      ) => {
        // Handle Escape key for canceling active turn
        if (e.key === 'Escape') {
          if (activeTurnId && onCancelTurn) {
            e.preventDefault()
            onCancelTurn(activeTurnId)
            return
          }
          return
        }
        // Only handle Enter key events
        if (e.key !== 'Enter') return
        // Allow Shift+Enter for new lines
        if (e.shiftKey) return
        // Handle Ctrl/Cmd+Enter for adding new line to input
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault()
          form.change('prompt', `${promptValue}\n`)
          return
        }
        // Prevent default Enter behavior
        e.preventDefault()
        // Validate form state before submission
        if (!valid) {
          console.warn('Form validation failed, cannot submit')
          return
        }

        if (!isSubmittable) {
          console.warn('Form is not submittable (disabled, loading, or submit disabled)')
          return
        }

        // Ensure we have valid input before submitting
        const validation = validate(form.getState().values)

        if (validation.prompt) {
          console.warn('Invalid prompt text provided')
          return
        }

        // Submit the form
        try {
          form.submit()
        } catch (error) {
          console.error('Error submitting form:', error)
        }
      },
      [activeTurnId, onCancelTurn, isSubmittable],
    )

    return (
      <Form<PromptFormValues>
        initialValues={{ prompt: initialPrompt ?? '', files: null }}
        decorators={[focusOnErrors]}
        onSubmit={(values, form) => {
          deleteStoredValue()
          if (clearOnSubmit) form.reset()
          onSubmit(values, form)
        }}
        validate={validate}
        render={({ handleSubmit, valid, form }) => (
          <form
            onSubmit={handleSubmit}
            className='flex w-full flex-col items-center justify-center'
          >
            <div className='relative flex w-full'>
              <Field<string> name='prompt'>
                {({ input, meta }) => (
                  <PromptTextArea
                    ref={ref}
                    fieldId={fieldId}
                    setValue={(value) => form.change('prompt', value)}
                    disabled={disabled || loading}
                    error={meta.invalid && input.value.length > 0}
                    placeholder={t('chat.promptTextarea.placeholder')}
                    className='font-light'
                    onKeyDown={(e) => handleKeyDown(e, form, input.value?.trim(), valid)}
                    {...input}
                  />
                )}
              </Field>
              <div className='absolute bottom-4 left-4 flex h-6 items-center gap-2'>
                {craftButtonState === 'loading' ? (
                  <div className='flex size-6 items-center justify-center rounded-full bg-mango-300'>
                    <AgentAnimation width={24} height={24} />
                  </div>
                ) : (
                  craftButtonState && (
                    <TooltipIconButton
                      size='auto'
                      keyName='chat.craftAgent.a11y'
                      onClick={onCraftAgentClick}
                      contentProps={{ align: 'start', side: 'bottom', sideOffset: 16 }}
                      disabled={!isSubmittable || craftButtonState === 'disabled'}
                      className='size-6 bg-mango-300 hover:bg-mango-300 focus:bg-mango-300 active:bg-mango-300 disabled:bg-neutral-150 disabled:opacity-35'
                    >
                      <CraftAgentIcon className='size-7 pt-[1px] text-neutral-white' />
                    </TooltipIconButton>
                  )
                )}
                {domainId !== DATA_INTELLIGENCE_DOMAIN_ID && (
                  <PromptFormFileUpload
                    disabled={!isSubmittable || craftButtonState === 'loading'}
                  />
                )}
              </div>
              <div className='absolute bottom-4 right-4 flex h-6 items-center justify-end'>
                {onCancelTurn && activeTurnId ? (
                  <TooltipIconButton
                    unstyled
                    size='auto'
                    titleString='Cancel Turn'
                    onClick={(e) => {
                      e.preventDefault()
                      if (onCancelTurn && activeTurnId) onCancelTurn(activeTurnId)
                    }}
                    className='size-6'
                    contentProps={{ align: 'end', side: 'bottom', sideOffset: 16 }}
                  >
                    <CancelTurnAnimation />
                  </TooltipIconButton>
                ) : (
                  <Button
                    type='submit'
                    disabled={!valid || !isSubmittable || craftButtonState === 'loading'}
                    title={t('home.taskForm.submit.a11y')}
                    className={cn(
                      'size-6 bg-transparent p-0 text-neutral-500 disabled:bg-transparent disabled:text-neutral-100',
                      buttonClassName,
                    )}
                    {...buttonProps}
                  >
                    {loading ? (
                      <Spinner className='opacity-60' color='black' size='xs' />
                    ) : (
                      <ArrowRightIcon className='h-5 w-5' />
                    )}
                  </Button>
                )}
              </div>
            </div>
            {renderFooter?.(form)}
          </form>
        )}
      />
    )
  },
)
PromptForm.displayName = 'PromptForm'

type PromptTextAreaProps = Readonly<
  TextareaAutosizeProps & {
    setValue: (value?: string) => void
    error?: boolean
    fieldId: string
  }
>

const PromptTextArea = React.forwardRef<HTMLTextAreaElement, PromptTextAreaProps>(
  ({ className, fieldId, setValue, error, value, ...props }, ref) => {
    const { t } = useTranslate()
    const isStoredValueLoaded = React.useRef(false)
    const textareaRef = React.useRef<HTMLTextAreaElement | null>(null)
    const { storedValue, setStoredValue, deleteStoredValue, localStorageKey } =
      usePromptLocalStorage(fieldId)

    // Combine external ref with internal ref
    const setRefs = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        textareaRef.current = node
        if (typeof ref === 'function') {
          ref(node)
        } else if (ref) {
          ref.current = node
        }
      },
      [ref],
    )

    React.useEffect(() => {
      if (typeof value === 'string' && !!value.trim()) {
        setStoredValue(value)
      } else {
        deleteStoredValue()
      }
    }, [value])

    React.useEffect(() => {
      setValue('')
      isStoredValueLoaded.current = false
    }, [localStorageKey])

    React.useEffect(() => {
      if (isStoredValueLoaded.current) return
      if (storedValue) setValue(storedValue)
      isStoredValueLoaded.current = true
    }, [isStoredValueLoaded.current, storedValue])

    // Fix height recalculation when tab becomes visible again
    React.useEffect(() => {
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible' && textareaRef.current) {
          // Use double requestAnimationFrame to ensure DOM is fully ready
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              const textarea = textareaRef.current
              if (!textarea) return

              // Force react-textarea-autosize to recalculate by resetting height
              // The library will recalculate based on content and minRows/maxRows
              textarea.style.height = 'auto'
              // Force browser reflow to ensure height reset is applied
              void textarea.offsetHeight
              // Trigger resize event that react-textarea-autosize listens to
              window.dispatchEvent(new Event('resize'))
              // The library will recalculate and set the correct height
            })
          })
        }
      }

      document.addEventListener('visibilitychange', handleVisibilityChange)
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }
    }, [])

    return (
      <TextareaAutoSize
        ref={setRefs}
        value={value}
        autoFocus
        variant='prompt'
        error={error}
        className={cn(
          'min-h-[100px] w-full pb-12 duration-100 ease-out placeholder:font-light placeholder:tracking-wide',
          value ? 'transition-[height]' : 'transition-none',
          error &&
            'rounded-lg border-2 border-error-300 bg-neutral-50 focus:border-error-300 focus:outline-0',
          className,
        )}
        aria-label={t('chat.promptTextarea.a11y')}
        minRows={1}
        maxRows={8}
        {...props}
      />
    )
  },
)
PromptTextArea.displayName = 'PromptTextArea'

export function usePromptLocalStorage(fieldId: string) {
  const { domainId } = useDomainFromUrl()
  const localStorageKey = `EmergenceChatPrompt_${fieldId}_${domainId}`
  const [storedValue, setStoredValue, deleteStoredValue] = useLocalStorage<string>(localStorageKey)

  return { storedValue, setStoredValue, deleteStoredValue, localStorageKey }
}
// Helper function to check if a string contains only special characters
function containsOnlySpecialCharacters(text: string): boolean {
  if (!text || !text.trim()) return false

  return !/[a-zA-Z0-9]/.test(text.trim())
}

function validate({ prompt, files }: PromptFormValues) {
  if (!prompt?.trim() && !files?.length) return { prompt: true }

  // Check if prompt contains only special characters
  if (prompt && containsOnlySpecialCharacters(prompt)) {
    return { prompt: 'specialCharsError' }
  }

  return {}
}

const focusOnErrors = createDecorator<PromptFormValues>()

export { PromptForm, type PromptFormValues }
